import { Injectable, OnDestroy } from '@angular/core';
import { ActivationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, filter, Subscription } from 'rxjs';

export interface IBreadcrumb {
  id: string;
  route: string;
  label: string;
}

@Injectable({
  providedIn: 'root',
})
export class EoBreadcrumbService implements OnDestroy {
  public breadcrumbSegments$ = new BehaviorSubject<IBreadcrumb[]>([]);
  public poppedSegments$ = new BehaviorSubject<IBreadcrumb[]>([]);
  private subscriptions: Subscription[] = [];

  constructor(private router: Router) {
    this.init();
    this.subscriptions = [
      this.getActivationEndSub(),
      this.getNavigationStartPopstateSub(),
      this.getbreadcrumbSegmentsSub(),
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private onNavigationStartPopState(e: NavigationStart): void {
    if (e.url === this.poppedSegments$.getValue().slice(-1)[0]?.route) {
      this.poppedSegments$.next(this.poppedSegments$.getValue().slice(0, -1));
    } else {
      const poppedSegment = this.pop();
      if (!poppedSegment) return;
      this.poppedSegments$.next([
        ...this.poppedSegments$.getValue(),
        poppedSegment,
      ]);
    }
  }

  private onActivationEnd(e: ActivationEnd): void {
    if (e?.snapshot?.url?.length === 0) return;

    if (this.getLastSegment()?.route === this.router.url) return;

    const b: IBreadcrumb = {
      id: new Date().getTime().toString(),
      route: this.router.url,
      label: e.snapshot.data?.['breadcrumb'],
    };
    this.push(b);
  }

  private onBreadcrumbChange(val: IBreadcrumb[]): void {
    sessionStorage.setItem('breadcrumb', JSON.stringify(val));
  }

  // Initialize breadcrumb segments
  init(): void {
    const breadcrumbLs = sessionStorage.getItem('breadcrumb');
    const breadcrumb = breadcrumbLs ? JSON.parse(breadcrumbLs) : [];
    this.set(breadcrumb);
    // this.skipNextNavigationEnd = true;
  }

  // Getter for breadcrumb segments
  get(): IBreadcrumb[] {
    return this.breadcrumbSegments$?.getValue();
  }

  // Setter for breadcrumb segments
  set(segments: IBreadcrumb[]): void {
    this.breadcrumbSegments$.next(segments);
  }

  // Clear breadcrumb segments
  clear(): void {
    this.breadcrumbSegments$.next([]);
  }

  // Push a breadcrumb segment
  push(segment: IBreadcrumb): void {
    let segments = this.breadcrumbSegments$.getValue();
    segments.push(segment);
    this.breadcrumbSegments$.next([...segments]);
  }

  // Pop a breadcrumb segment
  pop(): IBreadcrumb | undefined {
    let segments = this.breadcrumbSegments$.getValue();
    let poppedSegment = segments.pop();
    this.breadcrumbSegments$.next([...segments]);
    return poppedSegment;
  }

  popUntil(breadcrumbId: string): void {
    while (1) {
      let lastSegment = this.breadcrumbSegments$.getValue().pop();
      if (!lastSegment) break;
      if (lastSegment?.id === breadcrumbId) break;
    }
  }

  getLastSegment(): IBreadcrumb | undefined {
    let segments = this.breadcrumbSegments$.getValue();
    return segments[segments.length - 1];
  }

  private getNavigationStartPopstateSub(): Subscription {
    return this.router.events
      .pipe(filter((e: any) => e instanceof NavigationStart))
      .pipe(filter((e: any) => e.navigationTrigger === 'popstate'))
      .subscribe((event: any) => {
        this.onNavigationStartPopState(event);
      });
  }

  private getActivationEndSub(): Subscription {
    return this.router.events
      .pipe(filter((e) => e instanceof ActivationEnd))
      .subscribe((e: any) => {
        this.onActivationEnd(e);
      });
  }

  private getbreadcrumbSegmentsSub(): Subscription {
    return this.breadcrumbSegments$.subscribe((val) => {
      this.onBreadcrumbChange(val);
    });
  }
}
